import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Mail,
  Package,
  RefreshCw,
  Search,
  Inbox,
  UserPlus,
  Crown,
  X,
} from "lucide-react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth as getSecondaryAuth } from "firebase/auth";

import { auth, firebaseConfig, isSuperAdmin, SUPER_ADMIN_EMAIL } from "../lib/firebase";
import { listCollection } from "../lib/leads";
import { BRAND } from "../lib/brand";

const TABS = [
  { key: "wholesale_enquiries", label: "Wholesale Quotes" },
  { key: "enquiries", label: "Inline Enquiries" },
  { key: "quick_leads", label: "Popup Leads" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ---------------- Login screen ----------------
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // "login" | "reset" | "signup"

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "reset") {
        await sendPasswordResetEmail(auth, email);
        toast.success("Password reset email sent.");
        setMode("login");
      } else if (mode === "signup") {
        // First-time super-admin self-signup. Only allowed for the
        // designated super-admin email — managers are created from inside
        // the dashboard.
        if (email.trim().toLowerCase() !== SUPER_ADMIN_EMAIL) {
          throw new Error(
            `Self-signup is restricted to ${SUPER_ADMIN_EMAIL}. Ask the super-admin to create your account.`
          );
        }
        await createUserWithEmailAndPassword(auth, email.trim(), password);
        toast.success("Super-admin account created. Welcome!");
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast.success("Signed in.");
      }
    } catch (err) {
      const code = err?.code || "";
      const msg = code.includes("invalid-credential")
        ? "Invalid email or password."
        : code.includes("user-not-found")
        ? "No account found. If you are the super-admin, use 'First-time setup' to create your account."
        : code.includes("wrong-password")
        ? "Incorrect password."
        : code.includes("too-many-requests")
        ? "Too many attempts. Please try again later."
        : code.includes("email-already-in-use")
        ? "This email already has an account. Use Sign In instead."
        : code.includes("weak-password")
        ? "Password must be at least 6 characters."
        : code.includes("operation-not-allowed")
        ? "Email/Password sign-in is not enabled in Firebase Console → Authentication → Sign-in method."
        : err?.message || "Sign in failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07271E] text-white flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto flex items-center justify-center border border-[#C9A227] rounded-full">
            <Lock className="w-6 h-6 text-[#C9A227]" strokeWidth={1.4} />
          </div>
          <h1 className="font-serif text-3xl mt-6 text-white">Admin Access</h1>
          <p className="text-white/60 mt-3 font-light text-sm">
            {mode === "reset"
              ? "Enter your email to receive a reset link."
              : mode === "signup"
              ? `First-time setup for the super-admin (${SUPER_ADMIN_EMAIL}).`
              : "Sign in to view all enquiries."}
          </p>
        </div>
        <form
          onSubmit={submit}
          className="bg-white/5 border border-white/10 backdrop-blur-md p-7"
          data-testid="admin-login-form"
        >
          <label className="label-overline text-[#C9A227]">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            placeholder="you@example.com"
            className="input-luxury-dark mt-2"
            data-testid="admin-email-input"
            autoComplete="email"
            autoFocus
            required
          />

          {mode !== "reset" && (
            <>
              <label className="label-overline text-[#C9A227] mt-6 block">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                placeholder={mode === "signup" ? "At least 6 characters" : "••••••••"}
                className="input-luxury-dark mt-2"
                data-testid="admin-password-input"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                minLength={mode === "signup" ? 6 : undefined}
                required
              />
            </>
          )}

          {error && (
            <p
              role="alert"
              data-testid="admin-login-error"
              className="mt-3 text-xs text-[#FF8B6B] font-light"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !email || (mode !== "reset" && !password)}
            className="btn-gold w-full justify-center mt-7 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-login-btn"
          >
            {loading
              ? "Please wait..."
              : mode === "reset"
              ? "Send Reset Link"
              : mode === "signup"
              ? "Create Super-Admin Account"
              : "Sign In"}
          </button>

          <div className="flex items-center justify-between mt-5 text-xs">
            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "reset" : "login");
                setError("");
              }}
              className="text-white/60 hover:text-[#C9A227] transition-colors"
              data-testid="admin-toggle-reset"
            >
              {mode === "reset" ? "Back to sign in" : "Forgot password?"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode(mode === "signup" ? "login" : "signup");
                setError("");
              }}
              className="text-white/60 hover:text-[#C9A227] transition-colors"
              data-testid="admin-toggle-signup"
            >
              {mode === "signup" ? "Already have an account?" : "First-time setup"}
            </button>
          </div>

          <p className="text-[10px] text-white/40 mt-6 font-light leading-relaxed">
            Super-admin: <code className="text-[#C9A227]">{SUPER_ADMIN_EMAIL}</code>
            <br />
            First-time? Have the super-admin create your account from the
            dashboard.
          </p>
        </form>
      </div>
    </div>
  );
};

// ---------------- Add manager modal ----------------
const AddManagerModal = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Secondary-app pattern: create the new user without signing the admin out.
    const secondary = initializeApp(firebaseConfig, `secondary-${Date.now()}`);
    const secondaryAuth = getSecondaryAuth(secondary);
    try {
      await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
      await signOut(secondaryAuth);
      toast.success(`Manager account created: ${email.trim()}`);
      setEmail("");
      setPassword("");
      onCreated?.();
      onClose();
    } catch (err) {
      const code = err?.code || "";
      const msg = code.includes("email-already-in-use")
        ? "An account with this email already exists."
        : code.includes("weak-password")
        ? "Password must be at least 6 characters."
        : code.includes("invalid-email")
        ? "Invalid email format."
        : err?.message || "Could not create account.";
      setError(msg);
      toast.error(msg);
    } finally {
      try {
        await deleteApp(secondary);
      } catch {}
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      data-testid="add-manager-modal"
    >
      <div className="bg-[#FAF8F2] w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#0E4D3D]/60 hover:text-[#C9A227]"
          data-testid="add-manager-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#C9A227]/10 text-[#C9A227]">
              <UserPlus className="w-5 h-5" strokeWidth={1.4} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-[#0E4D3D]">
                Invite a Manager
              </h3>
              <p className="text-xs text-[#4A4A4A] font-light">
                Create access for a teammate
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-7 space-y-5">
            <div>
              <label className="label-overline text-[#0E4D3D]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                placeholder="manager@example.com"
                className="input-luxury mt-1"
                data-testid="add-manager-email"
                required
              />
            </div>
            <div>
              <label className="label-overline text-[#0E4D3D]">
                Temporary Password
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                placeholder="At least 6 characters"
                className="input-luxury mt-1"
                data-testid="add-manager-password"
                minLength={6}
                required
              />
              <p className="text-[10px] text-[#4A4A4A]/70 font-light mt-2">
                Share these credentials with the manager. They can change the
                password via "Forgot password" on the login screen.
              </p>
            </div>
            {error && (
              <p
                role="alert"
                className="text-xs text-red-700"
                data-testid="add-manager-error"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !email || password.length < 6}
              className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-manager-submit"
            >
              {loading ? "Creating..." : "Create Manager Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---------------- Dashboard helpers ----------------
const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-[#0E4D3D]/10 p-5 lift">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={1.4} />
      </div>
      <div className="label-overline text-[#4A4A4A]">{label}</div>
    </div>
    <div className="font-serif text-3xl md:text-4xl text-[#0E4D3D] mt-4">{value}</div>
  </div>
);

const renderRow = (tab, row) => {
  if (tab === "wholesale_enquiries") {
    return (
      <>
        <td className="px-4 py-4 font-medium text-[#0E4D3D]">{row.name}</td>
        <td className="px-4 py-4">
          <div className="flex flex-col gap-0.5 text-sm">
            <a href={`tel:${row.phone}`} className="text-[#0E4D3D] hover:text-[#C9A227]">
              {row.phone}
            </a>
            {row.email && (
              <a href={`mailto:${row.email}`} className="text-[#4A4A4A] text-xs hover:text-[#C9A227]">
                {row.email}
              </a>
            )}
          </div>
        </td>
        <td className="px-4 py-4 text-sm text-[#4A4A4A]">{row.product}</td>
        <td className="px-4 py-4 text-sm text-[#4A4A4A]">{row.quantity}</td>
        <td className="px-4 py-4 text-xs text-[#4A4A4A] font-light max-w-xs">
          {row.additional_requirements || <span className="text-[#4A4A4A]/40">—</span>}
        </td>
        <td className="px-4 py-4 text-xs text-[#4A4A4A] whitespace-nowrap">{fmt(row.created_at)}</td>
      </>
    );
  }
  if (tab === "enquiries") {
    return (
      <>
        <td className="px-4 py-4 font-medium text-[#0E4D3D]">{row.name}</td>
        <td className="px-4 py-4 text-sm">
          <a href={`tel:${row.mobile}`} className="text-[#0E4D3D] hover:text-[#C9A227]">
            {row.mobile}
          </a>
        </td>
        <td className="px-4 py-4 text-sm text-[#4A4A4A]">{row.business_name}</td>
        <td className="px-4 py-4 text-sm text-[#4A4A4A]">{row.location}</td>
        <td className="px-4 py-4 text-sm text-[#4A4A4A]">
          {row.requirement} · {row.quantity}
        </td>
        <td className="px-4 py-4 text-xs text-[#4A4A4A] whitespace-nowrap">{fmt(row.created_at)}</td>
      </>
    );
  }
  return (
    <>
      <td className="px-4 py-4 font-medium text-[#0E4D3D]">{row.name}</td>
      <td className="px-4 py-4 text-sm">
        <a href={`tel:${row.mobile}`} className="text-[#0E4D3D] hover:text-[#C9A227]">
          {row.mobile}
        </a>
      </td>
      <td className="px-4 py-4 text-sm text-[#4A4A4A]">{row.business_name}</td>
      <td className="px-4 py-4 text-xs text-[#C9A227]">{row.source}</td>
      <td className="px-4 py-4 text-xs text-[#4A4A4A] whitespace-nowrap">{fmt(row.created_at)}</td>
    </>
  );
};

const headers = {
  wholesale_enquiries: ["Name", "Contact", "Product", "Quantity", "Requirements", "Submitted"],
  enquiries: ["Name", "Mobile", "Business", "Location", "Need", "Submitted"],
  quick_leads: ["Name", "Mobile", "Business", "Source", "Submitted"],
};

// ---------------- Dashboard ----------------
const Dashboard = ({ user }) => {
  const [tab, setTab] = useState("wholesale_enquiries");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [counts, setCounts] = useState({});
  const [openAddManager, setOpenAddManager] = useState(false);
  const canManageTeam = isSuperAdmin(user);

  const load = async (currentTab = tab) => {
    setLoading(true);
    try {
      const docs = await listCollection(currentTab);
      setData((d) => ({ ...d, [currentTab]: docs }));
      setCounts((c) => ({ ...c, [currentTab]: docs.length }));
    } catch (err) {
      toast.error("Failed to load enquiries: " + (err?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const loadAllCounts = async () => {
    try {
      const all = await Promise.all(
        TABS.map((t) => listCollection(t.key, 500))
      );
      const next = {};
      TABS.forEach((t, i) => {
        next[t.key] = all[i].length;
      });
      setCounts(next);
      // Cache loaded data
      setData((d) => {
        const merged = { ...d };
        TABS.forEach((t, i) => {
          merged[t.key] = all[i];
        });
        return merged;
      });
    } catch (err) {
      toast.error("Failed to refresh: " + (err?.message || ""));
    }
  };

  useEffect(() => {
    loadAllCounts();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!data[tab]) load(tab);
    // eslint-disable-next-line
  }, [tab]);

  const rows = (data[tab] || []).filter((r) => {
    if (!query) return true;
    const blob = JSON.stringify(r).toLowerCase();
    return blob.includes(query.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      {/* Top bar */}
      <header className="bg-[#0E4D3D] text-white">
        <div className="container-luxury py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center border border-[#C9A227] rounded-full">
              <span className="font-serif text-[#C9A227] text-base leading-none">E</span>
            </div>
            <div>
              <div className="font-serif text-white text-lg">{BRAND.name}</div>
              <div className="label-overline text-[#C9A227]/80 text-[0.6rem]">Admin Dashboard</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 sm:gap-5 w-full sm:w-auto justify-start sm:justify-end">
            <div className="text-left sm:text-right">
              <div className="text-xs text-white/60 font-light">Signed in as</div>
              <div className="text-sm text-white flex items-center gap-2 justify-start sm:justify-end">
                {canManageTeam && (
                  <Crown className="w-3.5 h-3.5 text-[#C9A227]" />
                )}
                {user.email}
              </div>
            </div>
            {canManageTeam && (
              <button
                onClick={() => setOpenAddManager(true)}
                data-testid="open-add-manager"
                className="text-[#C9A227] hover:text-white transition-colors flex items-center gap-2 text-sm border border-[#C9A227]/50 hover:border-white px-3 py-2"
              >
                <UserPlus className="w-4 h-4" />
                Invite Manager
              </button>
            )}
            <button
              onClick={loadAllCounts}
              className="text-white/70 hover:text-[#C9A227] transition-colors flex items-center gap-2 text-sm"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => signOut(auth)}
              className="text-white/70 hover:text-[#C9A227] transition-colors flex items-center gap-2 text-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-luxury py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard
            icon={Package}
            label="Wholesale Quotes"
            value={counts.wholesale_enquiries ?? "—"}
            accent="bg-[#C9A227]/10 text-[#C9A227]"
          />
          <StatCard
            icon={Inbox}
            label="Inline Enquiries"
            value={counts.enquiries ?? "—"}
            accent="bg-[#0E4D3D]/10 text-[#0E4D3D]"
          />
          <StatCard
            icon={Mail}
            label="Popup Leads"
            value={counts.quick_leads ?? "—"}
            accent="bg-[#0E4D3D]/10 text-[#0E4D3D]"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-4">
          <div className="flex gap-1 border-b border-[#0E4D3D]/15 overflow-x-auto no-scrollbar w-full sm:w-auto whitespace-nowrap">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`px-5 py-3 text-sm transition-colors relative whitespace-nowrap ${
                  tab === t.key
                    ? "text-[#0E4D3D] font-medium"
                    : "text-[#4A4A4A] hover:text-[#0E4D3D]"
                }`}
              >
                {t.label}
                {tab === t.key && (
                  <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#C9A227]" />
                )}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]/60" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search records..."
              className="bg-white border border-[#0E4D3D]/15 pl-10 pr-4 py-2.5 text-sm w-full focus:outline-none focus:border-[#C9A227] transition-colors"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#0E4D3D]/10 overflow-x-auto" data-testid="admin-table-wrapper">
          <table className="w-full text-left">
            <thead className="bg-[#FAF8F2]">
              <tr>
                {headers[tab].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 label-overline text-[#0E4D3D] text-[0.65rem] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0E4D3D]/8">
              {loading && (
                <tr>
                  <td colSpan={headers[tab].length} className="text-center py-16 text-[#4A4A4A]">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={headers[tab].length} className="text-center py-20 text-[#4A4A4A]/70">
                    <Inbox className="w-10 h-10 mx-auto opacity-40" strokeWidth={1.2} />
                    <p className="mt-4 font-light">No records yet.</p>
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-[#FAF8F2]/60 transition-colors"
                    data-testid={`admin-row-${row.id}`}
                  >
                    {renderRow(tab, row)}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-[#4A4A4A]/60 mt-6 font-light italic">
          {rows.length} record{rows.length !== 1 ? "s" : ""} · sorted newest first
        </p>
      </main>

      <AddManagerModal
        open={openAddManager}
        onClose={() => setOpenAddManager(false)}
        onCreated={() => {}}
      />
    </div>
  );
};

// ---------------- Page entry ----------------
export default function AdminPage() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07271E] text-white/70">
        <div className="flex items-center gap-3 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (!user) return <AdminLogin />;
  return <Dashboard user={user} />;
}
